import { ApolloClient } from '@apollo/client';
import { keyBy } from 'lodash-es';

import {
  ILoyaltyOfferConfigsQuery,
  ILoyaltyOfferTemplatesQuery,
  IPlaceholderFieldsQuery,
  LoyaltyOfferConfigsDocument,
  LoyaltyOfferTemplatesDocument,
  PlaceholderFieldsDocument,
} from 'generated/sanity-graphql';
import {
  ILocaleSmartBlockContent,
  PlaceholderFieldsMap,
  TransformSmartBlockContentFn,
} from 'hooks/use-locale-smart-block-content/types';

import {
  AppPersonalizedOffer,
  EnginePersonalizedOffer,
  LoyaltyEngineId,
  SanityConfigOffer,
  buildAppPersonalizedOffer,
} from '../../types';

export const fetchPersonalizedOfferData = async ({
  client,
  uri,
  templatesIds = [],
  configOffersIds,
}: {
  client: ApolloClient<object>;
  uri: string;
  templatesIds?: string[];
  configOffersIds: string[];
}) => {
  const [configs, templates, placeholderFields] = await Promise.all([
    client.query<ILoyaltyOfferConfigsQuery>({
      query: LoyaltyOfferConfigsDocument,
      context: { uri },
      variables: {
        ids: configOffersIds,
      },
    }),
    client.query<ILoyaltyOfferTemplatesQuery>({
      query: LoyaltyOfferTemplatesDocument,
      context: { uri },
      variables: {
        ids: templatesIds,
      },
    }),
    client.query<IPlaceholderFieldsQuery>({
      query: PlaceholderFieldsDocument,
      context: { uri },
    }),
  ]);

  const allConfigOffers = configs?.data?.allConfigOffers || [];
  const configMap = keyBy(allConfigOffers, 'loyaltyEngineId') as Record<
    LoyaltyEngineId,
    SanityConfigOffer
  >;

  const allOfferTemplates = templates?.data?.allOfferTemplates || [];
  const templateMap = keyBy(allOfferTemplates, 'loyaltyEngineId');

  const allPlaceholderFields = placeholderFields?.data?.allPlaceholderFields || [];
  const placeholderFieldsMap = keyBy(allPlaceholderFields, '_id') as PlaceholderFieldsMap;

  return { configMap, templateMap, placeholderFieldsMap };
};

type Await<T> = T extends Promise<infer U> ? U : T;

export interface IMergePersonalizedData extends IPersonalizedData {
  engineOffer: EnginePersonalizedOffer;
  transformSmartBlockContent: TransformSmartBlockContentFn;
}
export interface IPersonalizedData {
  configMap: Await<ReturnType<typeof fetchPersonalizedOfferData>>['configMap'];
  templateMap: Await<ReturnType<typeof fetchPersonalizedOfferData>>['templateMap'];
  placeholderFieldsMap: Await<
    ReturnType<typeof fetchPersonalizedOfferData>
  >['placeholderFieldsMap'];
}

export const mergePersonalizedData = ({
  engineOffer,
  configMap,
  templateMap,
  placeholderFieldsMap,
  transformSmartBlockContent,
}: IMergePersonalizedData): AppPersonalizedOffer => {
  const { templateId, configId } = engineOffer || {};
  const config = configMap[configId || ''];
  const template = templateMap[templateId || ''];

  const { localizedImage, backgroundImage } = template || {};
  const hasLocalizedImage = Boolean(localizedImage?.locale?.app);

  const transformField = (blockContent: ILocaleSmartBlockContent | null) =>
    blockContent &&
    transformSmartBlockContent({
      blockContent,
      interpolationSourceObject: {
        engineOffer,
        template,
        config,
      },
      placeholderFieldsMap,
    });

  const baseOffer = buildAppPersonalizedOffer(config, engineOffer);
  const appPersonalizedOffer = {
    ...baseOffer,
    // Override offer config data with personalization from template
    ...(template && {
      name: transformField(template.name),
      description: transformField(template.description),
    }),
    ...(hasLocalizedImage && { localizedImage }),
    ...(backgroundImage && { backgroundImage }),
  } as const;

  return appPersonalizedOffer;
};
